import * as angular from 'angular';
import { getKeycloakInstance, initializeKeycloak } from '../../keycloakConfig';
import { StoredToken } from './StoredToken';
import * as constants from '../../common/constants';
import { KeycloakConfig } from '../../keycloakConfig';
import { ISessionStorageService } from './sessionStorage.service';


export interface IKeycloakService {
    changePassword(): Promise<void>;
    isKeycloakConfigured(): boolean;
    initializeKeycloak(keycloakConfig: KeycloakConfig): angular.IPromise<boolean>;
    logout(): angular.IPromise<void>;
    refreshToken(): angular.IPromise<boolean>;
}

export class KeycloakService implements IKeycloakService {
    static serviceId = 'keycloakService';

    static $inject = ['$q', 'sessionStorageService'];

    constructor(private _$q: angular.IQService, private _sessionStorageService: ISessionStorageService) { }

    initializeKeycloak(keycloakConfig: KeycloakConfig): angular.IPromise<boolean> {
        const deferred = this._$q.defer<boolean>();
        initializeKeycloak(keycloakConfig);
        const keycloak = getKeycloakInstance();

        if (!keycloak) {
            deferred.reject(new Error('Keycloak is not configured'));
        }

        try {
            // Initialize with options
            keycloak
                .init({
                    onLoad: 'login-required',
                    pkceMethod: 'S256'
                })
                .then((authenticated) => {
                    if (authenticated) {
                        this.handleAuthenticationSuccess()
                            .then(() => deferred.resolve(true))
                            .catch((err) => deferred.reject(err));
                    } else {
                        deferred.resolve(false);
                    }
                })
                .catch((error) => {
                    console.error('Keycloak initialization failed:', error);
                    const errorMessage =
                        error instanceof Error ? `Failed to initialize Keycloak: ${error.message}` : 'Failed to initialize Keycloak: Unknown error';
                    deferred.reject(new Error(errorMessage));
                });
        } catch (error) {
            console.error('Keycloak initialization failed:', error);
            const errorMessage = error instanceof Error ? `Failed to initialize Keycloak: ${error.message}` : 'Failed to initialize Keycloak: Unknown error';
            deferred.reject(new Error(errorMessage));
        }

        return deferred.promise;
    }

    private handleAuthenticationSuccess(): angular.IPromise<void> {
        const deferred = this._$q.defer<void>();
        const keycloak = getKeycloakInstance();
        if (keycloak?.token) {

            keycloak
                .loadUserInfo()
                .then((userInfo: any) => {
                    const storedInfo = this._sessionStorageService.getTypedFrom<StoredToken>(constants.authorizationDataKey);
                    const json: StoredToken = {
                        sessionId: keycloak.sessionId,
                        expirationDate: new Date(keycloak.tokenParsed?.exp * 1000).toISOString(),
                        token: keycloak.token
                    };
                    const stringify = JSON.stringify({ ...(storedInfo || {}), ...json });
                    this._sessionStorageService.setTo(constants.authorizationDataKey, stringify);
                    deferred.resolve();
                })
                .catch((err) => {
                    console.error('Failed to load user info from Keycloak:', err);
                    deferred.reject(err);
                });
        } else {
            deferred.resolve();
        }

        return deferred.promise;
    }

    isKeycloakConfigured(): boolean {
        const keycloak = getKeycloakInstance();
        return keycloak !== null;
    }

    logout(): angular.IPromise<void> {
        const deferred = this._$q.defer<void>();
        const keycloak = getKeycloakInstance();

        if (!keycloak) {
            deferred.reject(new Error('Keycloak is not initialized'));
        }
        else {
            keycloak
                .logout()
                .then(() => {
                    deferred.resolve();
                })
                .catch((error) => {
                    console.error('Keycloak logout failed:', error);
                    deferred.reject(error);
                });
        }

        return deferred.promise;
    }

    refreshToken(): angular.IPromise<boolean> {
        const deferred = this._$q.defer<boolean>();
        const keycloak = getKeycloakInstance();

        if (!keycloak) {
            deferred.reject(new Error('Keycloak is not initialized'));
            return deferred.promise;
        }

        keycloak
            .updateToken(-1)
            .then((refreshed) => {
                if (refreshed && keycloak?.token) {
                    return this.handleAuthenticationSuccess()
                        .then(() => deferred.resolve(true))
                        .catch((err) => deferred.reject(err));
                }
                deferred.resolve(refreshed);
            })
            .catch((error) => {
                deferred.reject(error);
            });
        return deferred.promise;
    }

    changePassword(): Promise<void> {
        const keycloak = getKeycloakInstance();
        if (keycloak) {
            return keycloak.login({
                action: 'UPDATE_PASSWORD'
            });
        }
    }
}
